/*
 * The floating button of the shop chat (templates/_partials/chat.widget.html.twig) and what goes with it, without the
 * chat itself: app.js starts this on every page, the window (assets/shop-chat.js) loads on the first click only. The
 * page is the same for everybody (it is cached), so everything of the visitor happens here, in the browser:
 *
 * - Whether the chat shows at all: the button and every entry of the pages stand hidden until the shop has said that
 *   SES can answer now (ChatController::status; without credit, at its daily budget or switched off it cannot - user
 *   24.10.2026). The answer counts a minute, for the next pages of the session as well; a test shows itself to
 *   testers only (the cookie of ?chat_test=1).
 * - The conversation that goes on: its ID stays in localStorage for a day. While there is one for the market of the
 *   page, the button carries a dot and says "Gespräch fortsetzen"; an answer that came while the window was closed
 *   makes the dot pulse until the window opens again.
 * - On the product page the button steps aside while the buy box is in view: the box has its own entry, and the button
 *   would cover its right edge (the Klarna button on phones, the chosen size on narrow desktops).
 * - On product and basket pages a teaser beside the button points to the chat, once per session and only when the
 *   visitor hesitates: 30 seconds on the page or three changes of size or variant. It waits while the cookie notice,
 *   the window, the mini basket or the buy box is in the way, and it opens the window on a click only. Shown, click and
 *   dismiss go to the data layer (track()); whether they go on to GA4 is up to GTM and the consent of the visitor.
 */

var STORAGE_KEY = 'md_chat';
var STORAGE_TTL = 24 * 60 * 60 * 1000;
var STATUS_KEY = 'md_chat_status';
var STATUS_TTL = 60 * 1000;
var TEST_COOKIE = /(?:^|;\s*)md_chat_test=1(?:;|$)/;
var TEASER_KEY = 'md_chat_teaser';
var CHANGES_KEY = 'md_chat_changes';
var TEASER_SECONDS = 30;
var TEASER_CHANGES = 3;
// A size, length, colour or other variant of the buy box, a row of the size table: each opens another product page.
var CHANGE = 'a.product-page-chip[href]:not([aria-current]), [data-product-page-size-rows] .product-page-size-row';

var launcher = null;
var label = '';
// The conversation when the browser keeps nothing (private mode, storage blocked): it lasts as long as the page.
var memory = null;

export function start() {
    var chat = document.getElementById('shop-chat');
    // Off, or a test the visitor is not part of.
    if (!chat || (chat.getAttribute('data-chat-mode') === 'test' && !TEST_COOKIE.test(document.cookie))) return;

    status(chat.getAttribute('data-status-uri'), function (available) {
        if (!available) return;
        document.querySelectorAll('[data-chat-entry]').forEach(function (entry) { entry.hidden = false; });
        launch();
    });
}

/**
 * SES stopped answering while the window was open (no credit, its daily budget): the window keeps its note with the
 * ways to the team, the button and the entries go - for this page and the next ones of the minute.
 */
export function pause() {
    keepStatus(false);
    document.querySelectorAll('[data-chat-entry]').forEach(function (entry) { entry.hidden = true; });
}

/**
 * Whether the chat can answer: the answer of the last minute, or the shop's (ChatController::status). The minute is
 * part of the address, so no copy at the edge outlives it; asked without cookies, so the shop starts no session.
 * No answer is no chat.
 */
function status(uri, then) {
    var known = knownStatus();
    if (known !== null) {
        then(known);
        return;
    }
    if (!uri || typeof window.fetch !== 'function') {
        then(false);
        return;
    }

    window.fetch(uri + '&m=' + Math.floor(Date.now() / STATUS_TTL), { credentials: 'omit', headers: { 'Accept': 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; })
        .then(function (answer) {
            var available = !!answer && answer.available === true;
            keepStatus(available);
            then(available);
        });
}

function knownStatus() {
    try {
        var known = JSON.parse(window.sessionStorage.getItem(STATUS_KEY) || 'null');
        return known && typeof known.available === 'boolean' && Date.now() - known.at < STATUS_TTL && Date.now() >= known.at ? known.available : null;
    } catch (e) {
        return null;
    }
}

function keepStatus(available) {
    try {
        window.sessionStorage.setItem(STATUS_KEY, JSON.stringify({ available: available, at: Date.now() }));
    } catch (e) { /* every page asks again */ }
}

/**
 * The button, once the chat can answer: its dot, its place beside the buy box, its teaser.
 */
function launch() {
    var dock = document.querySelector('[data-chat-dock]');
    // A page of the purchase has no button.
    if (!dock) return;

    launcher = dock.querySelector('[data-chat-launcher]');
    label = launcher.getAttribute('aria-label') || '';
    refresh();
    // Another tab of the shop, or the page coming back from the back-forward cache.
    window.addEventListener('storage', function (event) {
        if (event.key === STORAGE_KEY || event.key === null) refresh();
    });
    window.addEventListener('pageshow', function (event) {
        if (event.persisted) refresh();
    });

    watchBuyBox(dock);
    tease(dock);
}

/**
 * The conversation of the market of the page that goes on, or null: { id, locale, expires, unread }.
 */
export function conversation() {
    if (memory) return memory;
    try {
        var stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        return stored && typeof stored.id === 'string' && stored.expires > Date.now() && stored.locale === locale() ? stored : null;
    } catch (e) {
        return null;
    }
}

/**
 * Keeps the conversation for a day from its last answer: unread when that came while the window was closed.
 */
export function remember(id, unread) {
    save({ id: id, locale: locale(), expires: Date.now() + STORAGE_TTL, unread: !!unread });
}

/**
 * The window shows the conversation: its answers are read.
 */
export function read() {
    var current = conversation();
    if (current && current.unread) {
        current.unread = false;
        save(current);
    }
}

export function forget() {
    memory = null;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* nothing stored */ }
    refresh();
}

function save(entry) {
    memory = null;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (e) {
        memory = entry;
    }
    refresh();
}

/**
 * A page of another market or language (/nl-nl/ on the same host) has a conversation of its own.
 */
function locale() {
    var chat = document.getElementById('shop-chat');
    return chat ? chat.getAttribute('data-locale') : null;
}

function refresh() {
    if (!launcher) return;

    var current = conversation();
    var text = current ? launcher.getAttribute('data-label-continue') || label : label;
    if (current) {
        launcher.setAttribute('data-chat-state', current.unread ? 'unread' : 'running');
    } else {
        launcher.removeAttribute('data-chat-state');
    }
    launcher.setAttribute('aria-label', text);
    launcher.setAttribute('title', text);
}

/**
 * The button steps aside while any part of the buy box of the product page is in view.
 */
function watchBuyBox(dock) {
    var entry = document.querySelector('.product-page-box [data-chat-entry]');
    var box = entry ? entry.closest('.product-page-box') : null;
    if (!box || typeof IntersectionObserver !== 'function') return;

    new IntersectionObserver(function (entries) {
        dock.classList.toggle('shop-chat-dock-away', entries[entries.length - 1].isIntersecting);
    }).observe(box);
}

function tease(dock) {
    var teaser = dock.querySelector('[data-chat-teaser]');
    // Once per session: without a session to remember it in, never. Not for a visitor who already talks to the chat.
    if (!teaser || teased() || conversation()) return;

    var page = teaser.getAttribute('data-chat-teaser');
    var seconds = 0;
    var shown = false;
    var timer = null;

    function finish(state) {
        clearInterval(timer);
        document.removeEventListener('click', clicked);
        teaser.hidden = true;
        if (state) keep(TEASER_KEY, state);
    }

    // Opening the chat ends the teaser for the session; a change of size or variant counts towards it.
    function clicked(event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-chat-open]')) {
            finish('opened');
            return;
        }
        if (shown || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (target.closest(CHANGE)) keep(CHANGES_KEY, String(changes() + 1));
    }

    teaser.querySelector('[data-chat-teaser-open]').addEventListener('click', function () {
        track('chat_teaser_click', { chat_teaser_page: page });
        finish(null);
        launcher.click();
    });
    teaser.querySelector('[data-chat-teaser-close]').addEventListener('click', function () {
        track('chat_teaser_dismiss', { chat_teaser_page: page });
        finish('dismissed');
    });
    document.addEventListener('click', clicked);

    timer = setInterval(function () {
        if (document.visibilityState !== 'hidden') seconds++;
        var free = !dock.hidden
            && !dock.classList.contains('shop-chat-dock-away')
            && !document.documentElement.classList.contains('shop-chat-open')
            && !document.querySelector('.offcanvas.show, .modal.show')
            && !consentPending();
        if (shown) {
            teaser.hidden = !free;
            return;
        }
        if (!free || (seconds < TEASER_SECONDS && changes() < TEASER_CHANGES)) return;
        if (!keep(TEASER_KEY, 'shown')) {
            finish(null);
            return;
        }
        shown = true;
        teaser.hidden = false;
        track('chat_teaser_shown', { chat_teaser_page: page });
    }, 1000);
}

/**
 * The cookie notice is open, or still to come: it shows a moment after the page while the visitor has not decided.
 */
function consentPending() {
    var notice = document.getElementById('md_cookie_allow-undefined');
    return !/(?:^|;\s*)md_cookie_allow=/.test(document.cookie) || (!!notice && !notice.classList.contains('d-none'));
}

function teased() {
    try {
        return window.sessionStorage.getItem(TEASER_KEY) !== null;
    } catch (e) {
        return true;
    }
}

function changes() {
    try {
        return parseInt(window.sessionStorage.getItem(CHANGES_KEY), 10) || 0;
    } catch (e) {
        return 0;
    }
}

function keep(key, value) {
    try {
        window.sessionStorage.setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * An event of the chat for the data layer, also for shop-chat.js; whether it goes on to GA4 is up to GTM and the consent
 * of the visitor.
 */
export function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, data || {}));
}
